import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import { FloorMasterCleaningSchedule } from 'pdfs';
import { useBuilding, useZones } from 'store/apis';
import { selectCurrentUserInfo } from 'store/slices';
import { useAppSelector } from 'store/store.hook';
import { EZoneTargetCleaningType } from '../../../enums';

dayjs.extend(customParseFormat);

interface UseMasterCleaningScheduleReportProps {
  buildingId: number;
  start: string;
  end: string;
}

export const useMasterCleaningScheduleReport = ({ buildingId, start, end }: UseMasterCleaningScheduleReportProps) => {
  const { org } = useAppSelector(selectCurrentUserInfo);
  const {
    data: { address = '', city = '', state = '', zip = '', floors: _floors = [] },
    isFetching: loadingBuilding,
  } = useBuilding({ buildingId });
  const {
    data: { active: zones = [] },
    isFetching: loadingZones,
  } = useZones({ buildingId });

  const building = { organization: org?.name || '', address, city, state, zip };

  const serviceDate: [string, string] = [
    dayjs(new Date(start)).format('YYYY-MM-DD'),
    dayjs(new Date(end)).format('YYYY-MM-DD'),
  ];

  let floorObj: { [key: number]: FloorMasterCleaningSchedule } = {};

  if (building && zones && _floors && _floors.length > 0 && zones.length > 0) {
    floorObj =
      _floors.reduce(
        (prev, curr) => ({
          ...prev,
          [curr.id]: {
            title: curr.floor.toLocaleLowerCase().includes('floor')
              ? curr.floor.replace('floor', 'Floor')
              : `Floor ${curr.floor}`,
            zones: [],
          },
        }),
        floorObj,
      ) || {};

    floorObj = zones.reduce((prev, curr) => {
      const {
        target_cleaning_type: type,
        code,
        name: location,
        schedule,
        days_active: daysActive,
        target_interval: targetInterval,
        target_frequency: targetFrequency,
      } = curr;
      const cleaningTimes =
        type === EZoneTargetCleaningType.SCHEDULE
          ? schedule.map((item) => dayjs(item.time, 'HH:mm:ss').format('hh:mm A'))
          : [];

      let frequency;
      let interval;
      if (type === EZoneTargetCleaningType.SCHEDULE) {
        frequency = cleaningTimes.length;
        interval =
          // convert number of weekday into string
          daysActive?.replace(
            /(0)|(1)|(2)|(3)|(4)|(5)|(6)/g,
            (m: string) => ['S', 'M', 'T', 'W', 'TH', 'F', 'SA'][+m],
          ) ?? 'daily';
      } else {
        frequency = targetFrequency ?? 1;
        interval = targetInterval ?? 'daily';
      }

      const zone = { type, code, location, interval, frequency, cleaningTimes };

      if (floorObj[curr.building_floor_id] && floorObj[curr.building_floor_id].zones) {
        floorObj[curr.building_floor_id].zones.push(zone);
      }

      return floorObj;
    }, floorObj);
  }

  const floors = Object.values(floorObj).filter((item) => item.zones.length > 0);

  return { data: { building, serviceDate, floors }, isLoading: loadingBuilding || loadingZones };
};
